import {
  UserService,
  Field,
  FiltersRequest,
  ListResponse,
  User,
  Role,
} from "@gen/user/v1/user_pb";
import { client } from "./config";
import { Code, ConnectError } from "@connectrpc/connect";
import { PageRequest, Sort } from "./common";
import { Direction } from "@gen/tsam/v1/page_pb";

export async function getConnectedUser(): Promise<User | null> {
  try {
    const res = await client(UserService).getCurrent({});
    return res.user || null;
  } catch (e: unknown) {
    const connectError = ConnectError.from(e);
    if (connectError.code === Code.Unauthenticated) {
      return null;
    }
    throw e;
  }
}

export async function getUser(id: string): Promise<User | undefined> {
  return (await client(UserService).get({ id })).user;
}

export async function getUsers(
  page?: PageRequest,
  sort?: Sort,
  filters?: FiltersRequest,
): Promise<ListResponse> {
  let field: Field;
  switch (sort?.field) {
    case "email":
      field = Field.EMAIL;
      break;
    case "firstName":
      field = Field.FIRST_NAME;
      break;
    case "lastName":
      field = Field.LAST_NAME;
      break;
    case "role":
      field = Field.ROLE;
      break;
    default:
      field = Field.UNSPECIFIED;
      break;
  }

  let direction: Direction;
  switch (sort?.direction) {
    case "asc":
      direction = Direction.ASC;
      break;
    case "desc":
      direction = Direction.DESC;
      break;
    default:
      direction = Direction.UNSPECIFIED;
      break;
  }

  return await client(UserService).list({
    page,
    sort: {
      direction,
      field,
    },
    filters,
  });
}

export async function createUser(user: User): Promise<User> {
  const res = await client(UserService).create({
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    role: user.role,
    agencyId: user.agencyId,
    caisseIds: user.caisseIds,
  });
  if (!res.user) {
    throw new Error("User is null");
  }

  return res.user;
}

export async function updateUser(user: User): Promise<User> {
  const res = await client(UserService).update({
    id: user.id,
    role: user.role,
    agencyId: user.agencyId,
    caisseIds: user.caisseIds,
  });
  if (!res.user) {
    throw new Error("User is null");
  }

  return res.user;
}

export const canAccessAdmin = (user: User) =>
  user.role === Role.ADMIN ||
  user.role === Role.TRAVEL_AGENT ||
  user.role === Role.CAISSE_ASSOCIATE;

export function listEditableRoles(user: User): Role[] {
  switch (user.role) {
    case Role.ADMIN:
      return [Role.TRAVEL_AGENT, Role.CAISSE_ASSOCIATE];
    case Role.TRAVEL_AGENT:
      return [Role.TRAVEL_AGENT, Role.CAISSE_ASSOCIATE];
    default:
      return [];
  }
}

export async function forgetMe() {
  return client(UserService).forgetMe({});
}
